import { setSaveStatus } from 'components/editor/grid/reduxStore/saveStatusSlice';
import type { AxiosError } from 'axios';
import { useMutation, useQueryClient } from 'react-query';
import type { ContentControllerUpdateMutationRequest, UpdateContentDto } from '@proposify/document-service-api-schema';
import { DocumentSaveStatus } from 'components/editor/shared/models/DocumentSaveStatus';
import { apiDocumentServiceRepository } from 'services/repositories/implementations/ApiDocumentServiceRepository';
import type { UpdateDocumentPayloadByContentId } from 'services/documents/DocumentRepository';
import { ErrorCode } from 'services/socket/SocketEvents';
import { QueryKeys } from 'utils/queryKeys';
import { useAppDispatch, useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { useCheckDocumentLockStatus } from 'components/editor/hooks/useCheckDocumentLockStatus';
import { selectContentId } from 'components/editor/grid/reduxStore/documentSettingsSlice';

interface MutationContext {
  previousData: unknown;
}

export default function useUpdateDocumentProperties() {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  const contentId = useAppSelector(selectContentId);
  const checkDocumentLockStatus = useCheckDocumentLockStatus();

  return useMutation<void, AxiosError, UpdateDocumentPayloadByContentId, MutationContext>({
    mutationFn: async (payload) => {
      dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVING }));

      const { title, price, totalType, displayDocumentTotal, contentId } = payload;

      const documentServicePayload: ContentControllerUpdateMutationRequest = {
        ...(title != null && { name: title }),
        ...(price !== undefined && { price }),
        ...(totalType !== undefined && { totalType }),
        ...(displayDocumentTotal !== undefined && { displayDocumentTotal }),
      } as UpdateContentDto;

      await apiDocumentServiceRepository.patchContent({
        contentId,
        payload: documentServicePayload,
      });
    },
    onSuccess: (_, payload) => {
      queryClient.setQueryData(
        QueryKeys.getDocumentMetadataByContentId(contentId),
        (oldData: UpdateDocumentPayloadByContentId | undefined) => ({
          ...oldData,
          ...payload,
        })
      );
      queryClient.invalidateQueries(QueryKeys.getDocumentMetadataByContentId(contentId));

      dispatch(setSaveStatus({ status: DocumentSaveStatus.SAVED }));
    },
    onMutate: async (newData) => {
      await queryClient.cancelQueries(QueryKeys.getDocumentMetadataByContentId(contentId));

      const previousData = queryClient.getQueryData(QueryKeys.getDocumentMetadataByContentId(contentId));

      queryClient.setQueryData(QueryKeys.getDocumentMetadataByContentId(contentId), (old: any) => ({
        ...old,
        ...newData,
      }));

      return { previousData };
    },
    onError: (error, _, context) => {
      if (error.isAxiosError) {
        const status = error.response?.status;
        let errorCode;

        if (status === 423) {
          errorCode = ErrorCode.LOCKED;
          checkDocumentLockStatus(ErrorCode.LOCKED);
        }

        dispatch(setSaveStatus({ status: DocumentSaveStatus.NOT_SAVED, errCode: errorCode }));

        if (context?.previousData) {
          queryClient.setQueryData(QueryKeys.getDocumentMetadataByContentId(contentId), context.previousData);
        }
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries(QueryKeys.getDocumentMetadataByContentId(contentId));
    },
  });
}
